import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type {
  EntityType, EvidenceStrength, Significance, SourceLayer,
} from '@et/schema';

/** Os quatro tipos que a cor codifica. Nunca uma tradicao. */
export type LexiconTone = 'evidence' | 'objection' | 'claim' | 'source' | 'neutral';

export interface TagProps {
  readonly tone?: LexiconTone;
  readonly variant?: 'default' | 'solid' | 'dashed';
  readonly children: React.ReactNode;
  readonly className?: string;
}

export function Tag({ tone = 'neutral', variant = 'default', children, className }: TagProps) {
  return (
    <span className={clsx('et-tag', `et-tag--${tone}`, variant !== 'default' && `et-tag--${variant}`, className)}>
      {children}
    </span>
  );
}

const ENTITY_TONE: Partial<Record<EntityType, LexiconTone>> = {
  evidence: 'evidence',
  objection: 'objection',
  claim: 'claim',
  source: 'source',
  response: 'evidence',
  passage: 'source',
};

export function TypeTag({ type, className }: { type: EntityType; className?: string }) {
  const { t } = useTranslation('lexicon');
  return (
    <Tag tone={ENTITY_TONE[type] ?? 'neutral'} className={className}>
      {t(`entityType.${type}`)}
    </Tag>
  );
}

/**
 * A forca le-se pela forma — quatro tracos — e nunca pela cor.
 *
 * `ok`/`warn`/`bad` sao estados de sistema e o design system proibe-os na
 * avaliacao de evidencia: uma pedra "verde" e um papiro "vermelho" diriam
 * quem ganhou. Tracos preenchidos dizem "quanto" sem dizer "bom". Antes era
 * uma pilula cinzenta igual em tudo, e a coluna mais importante da lista era
 * a que menos se distinguia.
 *
 * `disputed` nao e um grau: fica sem tracos e com contorno tracejado.
 */
const STRENGTH_LEVEL: Record<EvidenceStrength, number> = {
  very_strong: 4, strong: 3, moderate: 2, weak: 1, disputed: 0,
};

export function StrengthTag({ strength, className }: { strength: EvidenceStrength; className?: string }) {
  const { t } = useTranslation('lexicon');
  const level = STRENGTH_LEVEL[strength] ?? 0;
  return (
    <Tag
      variant={strength === 'disputed' ? 'dashed' : 'default'}
      className={clsx('et-strength', className)}
    >
      {strength !== 'disputed' && (
        <span className="et-strength__bars" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => <i key={i} data-on={i < level ? 'true' : 'false'} />)}
        </span>
      )}
      {t(`strength.${strength}`)}
    </Tag>
  );
}

/**
 * §34 — a significancia vive na **relacao** evidencia -> claim, nunca na
 * evidencia. A mesma pedra e `supporting` para a crucificacao e `contextual`
 * para a ressurreicao, por isso o claim a que se refere e obrigatorio.
 */
export function SignificanceTag({
  significance, forClaim, className,
}: {
  readonly significance: Significance;
  readonly forClaim: string;
  readonly className?: string;
}) {
  const { t } = useTranslation('lexicon');
  const level = { critical: 3, major: 3, supporting: 2, contextual: 1 }[significance];
  return (
    <span
      className={clsx('et-significance', `et-significance--${significance}`, className)}
      data-significance={significance}
    >
      <span className="et-significance__bars" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <i key={i} data-on={i < level ? 'true' : 'false'} />
        ))}
      </span>
      {t('significance.forClaim', { level: t(`significance.${significance}`), claim: forClaim })}
    </span>
  );
}

/**
 * §65, §79 — obrigatorio e nunca ambiguo. E o que impede atribuir ao Alcorao
 * algo que so existe em Hadith, ou a Jesus uma tradicao posterior.
 */
export function SourceLayerTag({ layer, className }: { layer: SourceLayer; className?: string }) {
  const { t } = useTranslation('lexicon');
  return (
    <Tag variant="dashed" className={className}>
      {t(`sourceLayer.${layer}`)}
    </Tag>
  );
}

/** §16 — um bloco que caiu para a lingua canonica diz-o em vez de desaparecer. */
export function FallbackBadge({ locale = 'en', className }: { locale?: string; className?: string }) {
  const { t } = useTranslation('lexicon');
  return (
    <abbr className={clsx('et-fallback', className)} title={t('fallback.explanation')}>
      {locale.toUpperCase()}
    </abbr>
  );
}
